// src/components/Sidebar.jsx
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/config";

/**
 * Calendar Icon Component
 * Used for the calendar navigation menu item
 */
const CalendarIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

/**
 * Customers Icon Component
 * Used for the customers navigation menu item
 */
const CustomersIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

/**
 * Invoices Icon Component
 * Used for the invoices navigation menu item
 */
const InvoicesIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

/**
 * To Do Icon Component
 * Used for the to do list navigation menu item
 */
const TodoIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
    />
  </svg>
);

/**
 * Drivers Icon Component
 * Used for the drivers navigation menu item — a truck silhouette
 */
const DriversIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 7a1 1 0 011-1h9a1 1 0 011 1v9H3V7zm11 3h4l3 3v3h-7v-6zM5 19a2 2 0 100-4 2 2 0 000 4zm12 0a2 2 0 100-4 2 2 0 000 4z"
    />
  </svg>
);

/**
 * Chevron Down Icon Component
 * Used for dropdown indicators in submenus
 */
const ChevronDownIcon = () => (
  <svg
    className="w-4 h-4 transition-transform duration-200"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

/**
 * Sidebar Component
 *
 * Main navigation sidebar with calendar, customers, and invoices sections.
 * Features responsive design for both desktop and mobile views.
 *
 * Navigation Structure:
 * - Calendar: View and manage appointments and events
 * - Customers: Manage customer information and jobs
 * - Invoices: Manage invoices, payments, and billing
 *   - Invoice Status submenu:
 *     - Not Paid: View unpaid invoices
 *     - Paid: View paid invoices
 *     - Quoted: View quotes
 *     - Draft: View draft invoices
 *     - Instalment: View instalment invoices
 */
function Sidebar() {
  // State for mobile sidebar toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // State for calendar submenu toggle
  const [calendarSubmenuOpen, setCalendarSubmenuOpen] = useState(false);
  // State for invoice submenu toggle - default to open
  const [invoiceSubmenuOpen, setInvoiceSubmenuOpen] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Determine which navigation section is currently active
   * Based on the current URL path
   *
   * @returns {string} - The active section identifier
   */
  const getActiveSection = () => {
    const path = location.pathname;

    // Check for calendar-related routes
    if (path.includes("/calendar")) return "calendar";

    // Check for customer-related routes (including job and room pages)
    if (path.includes("/customers")) return "customers";

    // Check for to do list routes
    if (path.includes("/todo")) return "todo";

    // Check for driver-related routes
    if (path.includes("/drivers")) return "drivers";

    // Check for invoice-related routes
    if (path.includes("/invoices")) return "invoices";

    // Default to calendar for root path
    if (path === "/" || path === "/dashboard") return "calendar";

    return "calendar"; // Default fallback
  };

  const activeSection = getActiveSection();

  /**
   * Handle user logout
   * Signs out from Firebase and redirects to login page
   */
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  /**
   * Close mobile sidebar when navigation item is clicked
   * Improves mobile user experience
   */
  const handleMobileNavClick = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="sidebar-wrapper">
      {/* Desktop Sidebar - Hidden on mobile with CSS media queries (see index.css) */}
      <div className="desktop-sidebar-container flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-indigo-800">
            {/* Sidebar Header */}
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <h1 className="text-white text-2xl font-bold">
                  {import.meta.env.VITE_PUBLIC_ADMIN_NAME}
                </h1>
              </div>

              {/* Main Navigation Menu */}
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {/* Calendar Navigation with Submenu */}
                <div className="space-y-1">
                  {/* Main Calendar Link */}
                  <div className="flex items-center">
                    <Link
                      to="/calendar"
                      className={`${
                        activeSection === "calendar"
                          ? "bg-indigo-900 text-white"
                          : "text-indigo-100 hover:bg-indigo-700"
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md flex-1 transition-colors duration-200`}
                      title="View calendar events and appointments"
                    >
                      <CalendarIcon />
                      <span className="ml-3">Calendar</span>
                    </Link>
                    {/* Submenu Toggle Button */}
                    <button
                      onClick={() => setCalendarSubmenuOpen(!calendarSubmenuOpen)}
                      className={`${
                        activeSection === "calendar"
                          ? "text-white hover:bg-indigo-800"
                          : "text-indigo-100 hover:bg-indigo-700"
                      } p-1 rounded-md ml-1 transition-colors duration-200`}
                      title="Toggle event type submenu"
                    >
                      <div className={`transform transition-transform duration-200 ${
                        calendarSubmenuOpen ? 'rotate-180' : ''
                      }`}>
                        <ChevronDownIcon />
                      </div>
                    </button>
                  </div>

                  {/* Event Type Submenu */}
                  {calendarSubmenuOpen && (
                    <div className="ml-6 space-y-1">
                      <Link
                        to="/calendar?eventType=measurement"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View measurement appointments"
                      >
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        📏 Measurement
                      </Link>
                      <Link
                        to="/calendar?eventType=installation"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View installation appointments"
                      >
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        🔧 Installation
                      </Link>
                      <Link
                        to="/calendar?eventType=price"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View pricing consultations"
                      >
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                        💰 Pricing
                      </Link>
                      <Link
                        to="/calendar?eventType=follow-up"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View follow-up appointments"
                      >
                        <span className="w-2 h-2 bg-teal-400 rounded-full mr-2"></span>
                        📞 Follow-up
                      </Link>
                      <Link
                        to="/calendar?eventType=quote"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View quote appointments"
                      >
                        <span className="w-2 h-2 bg-slate-400 rounded-full mr-2"></span>
                        📋 Quote
                      </Link>
                      <Link
                        to="/calendar?eventType=reschedule"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View rescheduled appointments"
                      >
                        <span className="w-2 h-2 bg-orange-400 rounded-full mr-2"></span>
                        🔄 Reschedule
                      </Link>
                      <Link
                        to="/calendar?eventType=pickup"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View pickup appointments"
                      >
                        <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2"></span>
                        📋 Pick Up
                      </Link>
                      <Link
                        to="/calendar?eventType=delivery"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View delivery appointments"
                      >
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                        🚚 Delivery
                      </Link>
                    </div>
                  )}
                </div>

                {/* Customers Navigation */}
                <Link
                  to="/customers"
                  className={`${
                    activeSection === "customers"
                      ? "bg-indigo-900 text-white"
                      : "text-indigo-100 hover:bg-indigo-700"
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200`}
                  title="Manage customers and their jobs"
                >
                  <CustomersIcon />
                  <span className="ml-3">Customers</span>
                </Link>

                {/* To Do Navigation */}
                <Link
                  to="/todo"
                  className={`${
                    activeSection === "todo"
                      ? "bg-indigo-900 text-white"
                      : "text-indigo-100 hover:bg-indigo-700"
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200`}
                  title="Manage to do list"
                >
                  <TodoIcon />
                  <span className="ml-3">To Do</span>
                </Link>

                {/* Drivers Navigation (internal name "drivers"; user-facing label "Fitters") */}
                <Link
                  to="/drivers"
                  className={`${
                    activeSection === "drivers"
                      ? "bg-indigo-900 text-white"
                      : "text-indigo-100 hover:bg-indigo-700"
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200`}
                  title="Manage fitters and their queues"
                >
                  <DriversIcon />
                  <span className="ml-3">Fitters</span>
                </Link>

                {/* Invoices Navigation with Submenu */}
                <div className="space-y-1">
                  {/* Main Invoices Link */}
                  <div className="flex items-center">
                    <Link
                      to="/invoices"
                      className={`${
                        activeSection === "invoices"
                          ? "bg-indigo-900 text-white"
                          : "text-indigo-100 hover:bg-indigo-700"
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md flex-1 transition-colors duration-200`}
                      title="Manage invoices, payments, and billing"
                    >
                      <InvoicesIcon />
                      <span className="ml-3">Invoices</span>
                    </Link>
                    {/* Submenu Toggle Button */}
                    <button
                      onClick={() => setInvoiceSubmenuOpen(!invoiceSubmenuOpen)}
                      className={`${
                        activeSection === "invoices"
                          ? "text-white hover:bg-indigo-800"
                          : "text-indigo-100 hover:bg-indigo-700"
                      } p-1 rounded-md ml-1 transition-colors duration-200`}
                      title="Toggle invoice status submenu"
                    >
                      <div className={`transform transition-transform duration-200 ${
                        invoiceSubmenuOpen ? 'rotate-180' : ''
                      }`}>
                        <ChevronDownIcon />
                      </div>
                    </button>
                  </div>

                  {/* Invoice Status Submenu */}
                  {invoiceSubmenuOpen && (
                    <div className="ml-6 space-y-1">
                      <Link
                        to="/invoices?status=Not%20Paid%20yet"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View invoices that haven't been paid"
                      >
                        <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                        Not Paid
                      </Link>
                      <Link
                        to="/invoices?status=Paid"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View paid invoices"
                      >
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        Paid
                      </Link>
                      <Link
                        to="/invoices?status=Quote"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View quotes and estimates"
                      >
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                        Quoted
                      </Link>
                      <Link
                        to="/invoices?status=Draft"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View draft invoices"
                      >
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        Draft
                      </Link>
                      <Link
                        to="/invoices?status=Instalment"
                        className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-xs font-medium rounded-md w-full transition-colors duration-200"
                        title="View instalment invoices"
                      >
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                        Instalment
                      </Link>
                    </div>
                  )}
                </div>
              </nav>
            </div>

            {/* User Profile & Logout Section */}
            <div className="flex-shrink-0 flex border-t border-indigo-700 p-4">
              <button
                onClick={handleLogout}
                className="flex-shrink-0 w-full group block transition-colors duration-200 hover:bg-indigo-700 rounded-md p-2"
                title="Sign out of your account"
              >
                <div className="flex items-center">
                  <div>
                    <svg
                      className="h-9 w-9 text-indigo-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">Admin</p>
                    <p className="text-xs font-medium text-indigo-200 group-hover:text-white">
                      Sign out
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay - Rendered via Portal to escape overflow-hidden */}
      {sidebarOpen && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999
          }}
        >
          {/* Backdrop overlay - closes sidebar when clicked */}
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(75, 85, 99, 0.75)',
              zIndex: 1
            }}
          />

          {/* Mobile Sidebar Panel */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: '320px',
              maxWidth: '80vw',
              backgroundColor: '#3730a3',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
          {/* Close Button */}
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors duration-200"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Mobile Sidebar Content */}
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            {/* Mobile Header */}
            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-white text-xl font-bold">
                {import.meta.env.VITE_PUBLIC_ADMIN_NAME}
              </h1>
            </div>

            {/* Mobile Navigation Menu */}
            <nav className="mt-5 px-2 space-y-1">
              {/* Calendar Navigation with Submenu - Mobile */}
              <div className="space-y-1">
                {/* Main Calendar Link - Mobile */}
                <div className="flex items-center">
                  <Link
                    to="/calendar"
                    className={`${
                      activeSection === "calendar"
                        ? "bg-indigo-900 text-white"
                        : "text-indigo-100 hover:bg-indigo-700"
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md flex-1 transition-colors duration-200`}
                    onClick={handleMobileNavClick}
                  >
                    <CalendarIcon />
                    <span className="ml-3">Calendar</span>
                  </Link>
                  {/* Submenu Toggle Button - Mobile */}
                  <button
                    onClick={() => setCalendarSubmenuOpen(!calendarSubmenuOpen)}
                    className={`${
                      activeSection === "calendar"
                        ? "text-white hover:bg-indigo-800"
                        : "text-indigo-100 hover:bg-indigo-700"
                    } p-1 rounded-md ml-1 transition-colors duration-200`}
                    title="Toggle event type submenu"
                  >
                    <div className={`transform transition-transform duration-200 ${
                      calendarSubmenuOpen ? 'rotate-180' : ''
                    }`}>
                      <ChevronDownIcon />
                    </div>
                  </button>
                </div>

                {/* Event Type Submenu - Mobile */}
                {calendarSubmenuOpen && (
                  <div className="ml-6 space-y-1">
                    <Link
                      to="/calendar?eventType=measurement"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View measurement appointments"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                      📏 Measurement
                    </Link>
                    <Link
                      to="/calendar?eventType=installation"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View installation appointments"
                    >
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                      🔧 Installation
                    </Link>
                    <Link
                      to="/calendar?eventType=price"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View pricing consultations"
                    >
                      <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                      💰 Pricing
                    </Link>
                    <Link
                      to="/calendar?eventType=follow-up"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View follow-up appointments"
                    >
                      <span className="w-2 h-2 bg-teal-400 rounded-full mr-2"></span>
                      📞 Follow-up
                    </Link>
                    <Link
                      to="/calendar?eventType=quote"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View quote appointments"
                    >
                      <span className="w-2 h-2 bg-slate-400 rounded-full mr-2"></span>
                      📋 Quote
                    </Link>
                    <Link
                      to="/calendar?eventType=reschedule"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View rescheduled appointments"
                    >
                      <span className="w-2 h-2 bg-orange-400 rounded-full mr-2"></span>
                      🔄 Reschedule
                    </Link>
                    <Link
                      to="/calendar?eventType=pickup"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View pickup appointments"
                    >
                      <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2"></span>
                      📋 Pick Up
                    </Link>
                    <Link
                      to="/calendar?eventType=delivery"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View delivery appointments"
                    >
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                      🚚 Delivery
                    </Link>
                  </div>
                )}
              </div>

              {/* Customers Navigation - Mobile */}
              <Link
                to="/customers"
                className={`${
                  activeSection === "customers"
                    ? "bg-indigo-900 text-white"
                    : "text-indigo-100 hover:bg-indigo-700"
                } group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-200`}
                onClick={handleMobileNavClick}
              >
                <CustomersIcon />
                <span className="ml-3">Customers</span>
              </Link>

              {/* To Do Navigation - Mobile */}
              <Link
                to="/todo"
                className={`${
                  activeSection === "todo"
                    ? "bg-indigo-900 text-white"
                    : "text-indigo-100 hover:bg-indigo-700"
                } group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-200`}
                onClick={handleMobileNavClick}
              >
                <TodoIcon />
                <span className="ml-3">To Do</span>
              </Link>

              {/* Drivers Navigation - Mobile (user-facing label "Fitters") */}
              <Link
                to="/drivers"
                className={`${
                  activeSection === "drivers"
                    ? "bg-indigo-900 text-white"
                    : "text-indigo-100 hover:bg-indigo-700"
                } group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-200`}
                onClick={handleMobileNavClick}
              >
                <DriversIcon />
                <span className="ml-3">Fitters</span>
              </Link>

              {/* Invoices Navigation with Submenu - Mobile */}
              <div className="space-y-1">
                {/* Main Invoices Link - Mobile */}
                <div className="flex items-center">
                  <Link
                    to="/invoices"
                    className={`${
                      activeSection === "invoices"
                        ? "bg-indigo-900 text-white"
                        : "text-indigo-100 hover:bg-indigo-700"
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md flex-1 transition-colors duration-200`}
                    onClick={handleMobileNavClick}
                  >
                    <InvoicesIcon />
                    <span className="ml-3">Invoices</span>
                  </Link>
                  {/* Submenu Toggle Button - Mobile */}
                  <button
                    onClick={() => setInvoiceSubmenuOpen(!invoiceSubmenuOpen)}
                    className={`${
                      activeSection === "invoices"
                        ? "text-white hover:bg-indigo-800"
                        : "text-indigo-100 hover:bg-indigo-700"
                    } p-1 rounded-md ml-1 transition-colors duration-200`}
                    title="Toggle invoice status submenu"
                  >
                    <div className={`transform transition-transform duration-200 ${
                      invoiceSubmenuOpen ? 'rotate-180' : ''
                    }`}>
                      <ChevronDownIcon />
                    </div>
                  </button>
                </div>

                {/* Invoice Status Submenu - Mobile */}
                {invoiceSubmenuOpen && (
                  <div className="ml-6 space-y-1">
                    <Link
                      to="/invoices?status=Not%20Paid%20yet"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View invoices that haven't been paid"
                    >
                      <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                      Not Paid
                    </Link>
                    <Link
                      to="/invoices?status=Paid"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View paid invoices"
                    >
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                      Paid
                    </Link>
                    <Link
                      to="/invoices?status=Quote"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View quotes and estimates"
                    >
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                      Quoted
                    </Link>
                    <Link
                      to="/invoices?status=Draft"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View draft invoices"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                      Draft
                    </Link>
                    <Link
                      to="/invoices?status=Instalment"
                      className="text-indigo-200 hover:bg-indigo-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full transition-colors duration-200"
                      onClick={handleMobileNavClick}
                      title="View instalment invoices"
                    >
                      <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                      Instalment
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>

          {/* Mobile User Profile & Logout Section */}
          <div className="flex-shrink-0 flex border-t border-indigo-700 p-4">
            <button
              onClick={handleLogout}
              className="flex-shrink-0 group block w-full transition-colors duration-200 hover:bg-indigo-700 rounded-md p-2"
            >
              <div className="flex items-center">
                <div>
                  <svg
                    className="h-9 w-9 text-indigo-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">Admin</p>
                  <p className="text-xs font-medium text-indigo-200 group-hover:text-white">
                    Sign out
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
        </div>,
        document.body
      )}

      {/* Mobile Header Bar with Menu Toggle - Hidden on desktop with CSS */}
      <div className="mobile-header-container sticky top-0 z-10 items-center bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 shadow-sm">
        {/* Mobile Menu Toggle Button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 p-2 rounded-md transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Open sidebar menu"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Mobile Header Title */}
        <h1 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">
          {import.meta.env.VITE_PUBLIC_ADMIN_NAME}
        </h1>
      </div>
    </div>
  );
}

export default Sidebar;
